import {
  useEffect,
  useCallback,
  useState,
  useMemo,
  useContext,
  useImperativeHandle,
  forwardRef,
} from 'react'

import {
  Box,
  Button,
  getThemeColor,
  Icon,
  Text,
} from '@fairfashion/design-system'
import debounce from 'lodash/debounce'
import { useTheme } from 'styled-components'

import { GlobalDataContext } from '../../contexts/globalData'
import { CompanySegment } from '../../sdk/common/company.enum'
import {
  GetProvidersAlltiersMap,
  ICompanySegmentAlias,
} from '../../sdk/company/company.types'
import { sdk } from '../../services/sdk'
import { hexaColors } from '../../utilities/colors'
import { MapCard } from '../MapCard/map-card.comp'
import { MapMarkerProps } from '../MapCard/map-card.types'
import { ProvidersTreeModal } from '../ProvidersTreeModal/providers-tree-modal.comp'

import * as Styles from './providers-map.styles'
import { ProvidersMapProps } from './providers-map.types'

export const ProvidersMap = forwardRef(
  ({ maxHeight, header, providerId }: ProvidersMapProps, ref) => {
    const [openTreeModal, setOpenTreeModal] = useState<boolean>(false)

    const [zoom, setZoom] = useState<number>(4)
    const [bounds, setBounds] = useState<{
      northEastLat: number
      northEastLng: number
      southWestLat: number
      southWestLng: number
    }>({
      northEastLat: 0,
      northEastLng: 0,
      southWestLat: 0,
      southWestLng: 0,
    })

    const [providersMap, setProvidersMap] = useState<GetProvidersAlltiersMap[]>(
      [],
    )

    const { companySegmentType } = useContext(GlobalDataContext)

    const getProvidersMap = async () => {
      try {
        const _response = await sdk.company.getProvidersMap(
          zoom,
          bounds.northEastLat,
          bounds.northEastLng,
          bounds.southWestLat,
          bounds.southWestLng,
          providerId || undefined,
        )
        setProvidersMap(_response)
      } catch (error) {
        console.error(error)
      }
    }

    const debouncedGetProvidersMap = useCallback(
      debounce(() => {
        getProvidersMap()
      }, 500),
      [
        zoom,
        bounds.northEastLat,
        bounds.northEastLng,
        bounds.southWestLat,
        bounds.southWestLng,
      ],
    )

    useEffect(() => {
      debouncedGetProvidersMap()

      return () => {
        debouncedGetProvidersMap.cancel()
      }
    }, [
      zoom,
      bounds.northEastLat,
      bounds.northEastLng,
      bounds.southWestLat,
      bounds.southWestLng,
    ])

    useImperativeHandle(ref, () => ({
      refreshProvidersMap: getProvidersMap,
    }))

    const theme = useTheme()

    const locations: MapMarkerProps[] = useMemo(() => {
      const _locations: MapMarkerProps[] = []

      providersMap?.forEach((provider) => {
        if (provider.type === 'cluster') {
          if (!provider.lat || !provider.lng) return

          _locations.push({
            title: `${provider.count} Fornecedores`,
            description: `${provider.count} Fornecedores`,
            color: getThemeColor('secondary', theme),
            lat: provider.lat,
            lng: provider.lng,
            type: 'cluster',
            count: provider.count,
          })
        } else {
          const companySegment =
            companySegmentType?.[provider.data?.segment as CompanySegment] ??
            ({} as ICompanySegmentAlias)

          if (!provider.data?.address?.geoCoords?.coords) return

          _locations.push({
            title: provider.data?.name ?? '',
            description: companySegment?.translate ?? '',
            color: companySegment?.color ?? hexaColors[0],
            lat: provider.data?.address?.geoCoords?.coords?.lat,
            lng: provider.data?.address?.geoCoords?.coords?.lng,
            url: provider.data?.taxId
              ? `/providers/${provider.data?.taxId}`
              : undefined,
            type: 'individual',
          })
        }
      })

      return _locations
    }, [providersMap, companySegmentType, theme])

    const HeaderMap = () => {
      return (
        <Styles.ProvidersMapContainer direction="column" gap={1} id="map">
          <Box
            width="100%"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={3}
          >
            <Box alignItems="center" gap={1}>
              <Text>Mapa do desmatamento</Text>
            </Box>
            <Button
              fontSize={theme.typography.body_size.small}
              variant="outlined"
              padding="10px 14px"
              onClick={() => setOpenTreeModal(true)}
            >
              <Icon name="node_tree" size={16} color="grey_1" />
              Árvore de Relacionamento
            </Button>
          </Box>
          <Text color="grey_3" size="small">
            O mapa de desmatamento indica a localização áreas de riscos
            socioambientais.
            <br />
          </Text>
        </Styles.ProvidersMapContainer>
      )
    }

    return (
      <Box width="100%" position="relative">
        <MapCard
          center={locations[0]?.lat ? locations[0] : undefined}
          locations={locations}
          header={header || <HeaderMap />}
          maxHeight={maxHeight || '638px'}
          mapOptions={{
            setZoom: (zoom) => setZoom(zoom),
            setBounds: (bounds) =>
              setBounds({
                northEastLat: bounds.getNorthEast().lat,
                northEastLng: bounds.getNorthEast().lng,
                southWestLat: bounds.getSouthWest().lat,
                southWestLng: bounds.getSouthWest().lng,
              }),
          }}
        />
        <ProvidersTreeModal
          open={openTreeModal}
          onClose={() => setOpenTreeModal(false)}
          rootId={providerId || undefined}
        />
      </Box>
    )
  },
)
